import { Link, useNavigate } from 'react-router-dom';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { useEstimates } from '../hooks/useEstimates';
import { integrationBadgeStatus, m365StateLabel, useIntegrationStatus } from '../hooks/useIntegrationStatus';
import { formatDate } from '../lib/formatters';
import type { EstimateListRow } from '../lib/estimateTypes';

function Spinner() {
  return (
    <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
      <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
      <span className="sr-only">Loading mailbox</span>
    </div>
  );
}

function EstimateRowLink({ e, detail }: { e: EstimateListRow; detail: string }) {
  return (
    <Link
      to={`/estimates/${e.id}`}
      className="block w-full text-left min-h-11 p-3 rounded-md bg-surface-2 hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-text truncate">
          {e.customer?.name ?? e.contact_name ?? e.estimate_number}
          <span className="ml-2 text-xs font-mono text-text-faint">{e.estimate_number}</span>
        </p>
        <StatusBadge status={e.status ?? 'sent'} />
      </div>
      <p className="text-xs text-text-muted mt-0.5">{detail}</p>
    </Link>
  );
}

export function MailboxPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useEstimates();
  const { data: snapshot, isLoading: snapshotLoading, error: snapshotError } = useIntegrationStatus();

  const estimates = data ?? [];
  const sent = estimates.filter((e) => e.proposal_sent_at || e.status === 'sent' || e.status === 'viewed');
  const withReply = sent.filter((e) => e.m365_last_reply_at);
  const awaiting = sent.filter((e) => !e.m365_last_reply_at);

  const m365 = snapshot?.m365;
  const m365Connected = m365?.state === 'live';

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-text">Mailbox</h1>
        <p className="text-sm text-text-muted mt-0.5">
          Proposal sends and replies recorded on estimates. Mail goes out from the PazNetworks sales mailbox through Microsoft 365.
        </p>
      </div>

      {/* M365 connection state — derived from integration_status_snapshot() */}
      <section
        className="rounded-lg border border-border bg-surface p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        aria-labelledby="m365-state-heading"
      >
        <div className="min-w-0">
          <h2 id="m365-state-heading" className="text-sm font-semibold text-text">Microsoft 365</h2>
          {snapshotLoading && <p className="text-xs text-text-muted mt-0.5">Checking integration status…</p>}
          {snapshotError && (
            <p className="text-xs text-error mt-0.5" role="alert">
              Integration status unavailable: {snapshotError instanceof Error ? snapshotError.message : 'request failed'}
            </p>
          )}
          {m365 && !m365Connected && (
            <p className="text-xs text-text-muted mt-0.5">
              M365 not connected: no proposal has been delivered through Microsoft 365 from this system yet
              ({m365.with_message_id} of {m365.estimates_sent} sent estimates carry a mailbox message id).
              Sending is disabled until the Graph connection is configured and a send succeeds.
            </p>
          )}
          {m365 && m365Connected && (
            <p className="text-xs text-text-muted mt-0.5">
              {m365.with_message_id} of {m365.estimates_sent} sent estimates are tracked in the sales mailbox.
            </p>
          )}
        </div>
        {m365 && <StatusBadge status={integrationBadgeStatus(m365.state)} className="self-start sm:self-auto" />}
        {m365 && <span className="sr-only">{m365StateLabel(m365.state)}</span>}
      </section>

      {error && (
        <p className="text-sm text-error" role="alert">
          {error instanceof Error ? error.message : 'Estimates could not be loaded.'}
        </p>
      )}

      {sent.length === 0 ? (
        <EmptyState
          icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>}
          title="No proposals sent"
          subtitle="Estimates appear here once a proposal has been sent."
          action={{ label: 'View proposals', onClick: () => navigate('/proposals') }}
        />
      ) : (
        <div className="space-y-4">
          <section className="bg-surface rounded-lg border border-border p-4" aria-labelledby="replies-heading">
            <h2 id="replies-heading" className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Replies</h2>
            {withReply.length === 0 ? (
              <p className="text-sm text-text-muted">No replies recorded.</p>
            ) : (
              <div className="space-y-1.5">
                {withReply.map((e) => (
                  <EstimateRowLink key={e.id} e={e} detail={`Last reply ${formatDate(e.m365_last_reply_at)}`} />
                ))}
              </div>
            )}
          </section>

          <section className="bg-surface rounded-lg border border-border p-4" aria-labelledby="awaiting-heading">
            <h2 id="awaiting-heading" className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Awaiting reply</h2>
            {awaiting.length === 0 ? (
              <p className="text-sm text-text-muted">Nothing waiting.</p>
            ) : (
              <div className="space-y-1.5">
                {awaiting.map((e) => (
                  <EstimateRowLink
                    key={e.id}
                    e={e}
                    detail={
                      e.proposal_sent_at
                        ? `Sent ${formatDate(e.proposal_sent_at)}${e.m365_message_id ? '' : ' — no mailbox message id recorded'}`
                        : 'Marked sent, but no send timestamp recorded'
                    }
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
